import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { SignatureBg } from "../components/SignatureBg";
import { staffInviteApi } from "../lib/api";
import { PasswordRequirements, passwordOk } from "../components/PasswordRequirements";

const ROLE_LABEL: Record<string, string> = {
  manager: "Manager", analyst: "Analyst", viewer: "Viewer", owner: "Owner",
};

export default function StaffInvitePage() {
  const nav = useNavigate();
  const { token = "" } = useParams();
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState<{ email: string; name: string | null; role: string; company_name: string } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    staffInviteApi.validate(token)
      .then((r) => { setInvite(r.invite); setName(r.invite.name || ""); })
      .catch((e: any) => setLoadError(e?.message || "This invite link is not valid."))
      .finally(() => setLoading(false));
  }, [token]);

  const pwOk = passwordOk(password);
  const mismatch = confirm.length > 0 && confirm !== password;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!pwOk) { setError("Please meet all the password requirements below."); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setBusy(true); setError(null);
    try {
      await staffInviteApi.accept(token, password, name.trim() || undefined);
      nav("/dashboard", { replace: true }); // accept signs you in
    } catch (e: any) {
      setError(e?.message || "Could not accept this invite.");
      setBusy(false);
    }
  }

  return (
    <div className="login-page has-signature-bg">
      <SignatureBg />
      <div className="login-card">
        <div className="brand-lockup login-brand">
          <div className="brand-mark">
            <img src="/brand-mark.png" alt="Alief Dispute Hub" />
          </div>
          <div>
            <strong className="brand-wordmark">{invite ? invite.company_name : "Alief Dispute Hub"}</strong>
            <span>Analyze Smarter. Dispute Better.</span>
          </div>
        </div>

        {loading ? (
          <p className="muted" style={{ marginTop: 8 }}>Checking your invite…</p>
        ) : loadError ? (
          <>
            <h1>Invite unavailable</h1>
            <p className="error-text" role="alert" style={{ marginTop: 4 }}>{loadError}</p>
            <button className="ghost-button wide-button" style={{ marginTop: 12 }} onClick={() => nav("/login")}>
              Go to sign-in
            </button>
          </>
        ) : (
          <>
            <h1 id="invite-heading">Join the team</h1>
            <p className="muted" style={{ margin: "0 0 8px" }}>
              You're joining <strong>{invite!.company_name}</strong> as {ROLE_LABEL[invite!.role] || invite!.role}.
              Set a password to finish.
            </p>
            <form onSubmit={submit} className="stacked-form" aria-labelledby="invite-heading">
              <label className="form-field">
                <span>Email</span>
                <input type="email" value={invite!.email} readOnly disabled />
              </label>
              <label className="form-field">
                <span>Your name</span>
                <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" placeholder="First Last" />
              </label>
              <label className="form-field">
                <span>Password</span>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password" placeholder="At least 8 characters" required />
                <PasswordRequirements password={password} />
              </label>
              <label className="form-field">
                <span>Confirm password</span>
                <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password" placeholder="Re-enter your password" required />
                {mismatch && <span className="error-text">Passwords don't match.</span>}
              </label>
              {error && <p className="error-text" role="alert">{error}</p>}
              <button className="primary-button wide-button" disabled={busy || !pwOk || password !== confirm}>
                {busy ? "Setting up…" : "Set password & sign in"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
